import { Address, Postcode } from '@dreryk/address-common-api';

import { addressFormatter, BindingCreator, FormContextValues, TranslatableText } from '@dreryk/react-common';
import { AutoLayout, Text } from '@dreryk/react-components';

import { CityInput } from './CityInput';
import { FlatInput } from './FlatInput';
import { HealthcareBasicData } from './HealthcareBasicData';
import { HouseInput } from './HouseInput';
import { PostcodeInput } from './PostcodeInput';
import { StreetInput } from './StreetInput';
import { UsingParentAddressDataCheckbox } from './UsingParentAddressDataCheckbox';

type WithParentAddressFormBean = {
   usingParentAddressData: true;
   city?: never;
   postcode?: never;
   street?: never;
   house?: never;
   flat?: never;
};

type WithOwnAddressFormBean = {
   usingParentAddressData: false;
   city: string | null;
   postcode: Postcode | null;
   street: string | null;
   house: string | null;
   flat: string | null;
};

export type HealthcareAddressFormBean = WithParentAddressFormBean | WithOwnAddressFormBean;

export const createHealthcareAddressFormBean = (
   usingParentAddressData: boolean,
   address: Address | undefined
): HealthcareAddressFormBean => {
   if (usingParentAddressData) {
      return {
         usingParentAddressData: true,
      };
   }

   if (!address) {
      throw new Error('Address cannot be empty');
   }

   return {
      usingParentAddressData: false,
      city: address.findTown()?.getName() ?? null,
      postcode: address.findPostcode() ?? null,
      street: address.findStreet()?.asString() ?? null,
      house: address.findBuilding()?.asString() ?? null,
      flat: address.findFlat()?.asString() ?? null,
   };
};

const addressFormPartVisible = (
   register: BindingCreator<HealthcareAddressFormBean>,
   usingParentAddressData: boolean
): register is BindingCreator<HealthcareAddressFormBean & { usingParentAddressData: false }> => {
   return !usingParentAddressData;
};

type HealthcareAddressFormPartProps = Pick<FormContextValues<HealthcareAddressFormBean>, 'register'> & {
   readonly usingParentAddressData: boolean;
   readonly usingParentAddressDataLabel: TranslatableText;
   readonly healthcareParentData: HealthcareBasicData;
};

export const HealthcareAddressFormPart = (props: HealthcareAddressFormPartProps): JSX.Element => {
   return (
      <AutoLayout as={'div'} $direction={'vertical'} $spacing={12}>
         <UsingParentAddressDataCheckbox
            name={'usingParentAddressData'}
            register={props.register}
            label={props.usingParentAddressDataLabel}
         />
         {addressFormPartVisible(props.register, props.usingParentAddressData) ? (
            <>
               <AutoLayout as={'div'} $direction={'horizontal'} $spacing={36}>
                  <AutoLayout
                     as={CityInput}
                     name={'city'}
                     register={props.register}
                     required
                     $horizontalResizing={357}
                  />
                  <AutoLayout
                     as={PostcodeInput}
                     name={'postcode'}
                     register={props.register}
                     required
                     $horizontalResizing={126}
                  />
               </AutoLayout>

               <AutoLayout as={'div'} $direction={'horizontal'} $spacing={36}>
                  <AutoLayout as={StreetInput} name={'street'} register={props.register} $horizontalResizing={357} />
                  <AutoLayout
                     as={HouseInput}
                     name={'house'}
                     register={props.register}
                     required
                     $horizontalResizing={126}
                  />
                  <AutoLayout as={FlatInput} name={'flat'} register={props.register} $horizontalResizing={126} />
               </AutoLayout>
            </>
         ) : (
            <Text testId={'inheritedAddress'}>{addressFormatter(props.healthcareParentData.address)}</Text>
         )}
      </AutoLayout>
   );
};
